import { useState, useEffect, useCallback } from "react";

/**
 * Lightweight localStorage-backed collection store.
 *
 * This is the DEMO persistence layer. It seeds from mock data on first load,
 * then persists every change to localStorage so edits survive refreshes.
 *
 * When a real backend (Readdy Backend or SaaS Supabase) is connected, replace
 * the internals of this hook with database reads/writes — the calling code
 * (add / update / remove) stays the same.
 */
export function useCollection<T extends { id: string }>(storageKey: string, seed: T[]) {
  const [items, setItems] = useState<T[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as T[];
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore corrupt storage and fall back to seed
    }
    return seed;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch {
      // storage may be unavailable; ignore for demo
    }
  }, [storageKey, items]);

  const add = useCallback((item: T) => {
    setItems((prev) => [item, ...prev]);
  }, []);

  const update = useCallback((id: string, patch: Partial<T>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const reset = useCallback(() => {
    setItems(seed);
  }, [seed]);

  return { items, add, update, remove, reset };
}